// src/pages/healthcare/appointments/components/AddAvailabilityModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Modal, Select, Space, Spin, Tag, Typography } from "antd";
import { Link } from "react-router-dom";

import { dayjs } from "../../../../utils/time";
import { fetchScheduleOverrides } from "../../../../api/admin/scheduleOverrideApi";
import { WallClockWindow, mergeWallClockWindows, overrideFor, toDateKey, weeklyWindowsFor } from "../../../../utils/appointments/availability";
import { useAddAvailabilityWindow } from "../../../../hooks/appointment/usePractitionerAvailability";
import { ScheduleOverride, TeamMember } from "../../../../types/admin/TeamTypes";
import { SlotSelection } from "./SlotActionPopover";
import type { UserUuid } from "../../../../types/identity";

interface AddAvailabilityModalProps {
	selection: SlotSelection;
	teamMembers: TeamMember[];
	canManageSchedule: boolean;
	// The column's practitioner, or the user themselves from the all-staff week; null when there is no one to pre-pick.
	defaultPractitionerId: UserUuid | null;
	userId?: UserUuid | null;
	onClose: () => void;
}

const AddAvailabilityModal: React.FC<AddAvailabilityModalProps> = ({ selection, teamMembers, canManageSchedule, defaultPractitionerId, userId, onClose }) => {
	const addAvailability = useAddAvailabilityWindow();
	// D6: one override row per write, so a week drag picks exactly one practitioner.
	const pickable = selection.practitionerId === null;
	const [practitionerId, setPractitionerId] = useState<UserUuid>(defaultPractitionerId ?? "");
	const member = useMemo(() => teamMembers.find((candidate) => candidate.id === practitionerId) ?? null, [teamMembers, practitionerId]);
	const dateKey = toDateKey(selection.start);

	const [loading, setLoading] = useState<boolean>(true);
	const [loadFailed, setLoadFailed] = useState<boolean>(false);
	const [existing, setExisting] = useState<ScheduleOverride | null>(null);

	// Read-modify-write on a shared row, so the merge is computed from a fresh read rather than the calendar's cache.
	useEffect(() => {
		let cancelled = false;
		setExisting(null);
		setLoadFailed(false);
		// Nothing to read until someone is picked, and a read for the previous pick must not land on the new one.
		if (!practitionerId) {
			setLoading(false);
			return;
		}
		setLoading(true);

		fetchScheduleOverrides(practitionerId, dateKey, dateKey)
			.then(({ data }) => {
				if (cancelled) return;
				setExisting(overrideFor(data, selection.start));
			})
			.catch(() => {
				if (!cancelled) setLoadFailed(true);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [practitionerId, dateKey]);

	const dragged: WallClockWindow = useMemo(
		() => ({ start: dayjs(selection.start).format("HH:mm"), end: dayjs(selection.end).format("HH:mm") }),
		[selection.start, selection.end]
	);

	const coversOneDate = existing !== null && String(existing.start_date).slice(0, 10) === dateKey && String(existing.end_date).slice(0, 10) === dateKey;
	const refused = existing !== null && (existing.override_type !== "custom" || !coversOneDate);

	const merged = useMemo(() => {
		const base = existing === null ? weeklyWindowsFor(member, selection.start) : mergeWallClockWindows(existing.schedule_data ?? []);
		return mergeWallClockWindows([...base, dragged]);
	}, [existing, member, selection.start, dragged]);

	const practitionerOptions = teamMembers.map((candidate) => ({ value: candidate.id, label: `${candidate.first_name} ${candidate.last_name}`.trim() }));

	const handleSave = () => {
		if (refused || loadFailed || !practitionerId) return;

		addAvailability.mutate({
			userId: practitionerId,
			overrideId: existing?.id ?? null,
			dateKey,
			// ⚠️ HH:mm WALL CLOCK. The block path sends UTC instants; the two are never interchangeable.
			windows: merged,
		});
		onClose();
	};

	return (
		<Modal
			open
			title="Add availability"
			okText={existing === null ? "Add availability" : "Update this day"}
			okButtonProps={{ disabled: loading || refused || loadFailed || !practitionerId, "data-testid": "appointments-add-availability-save" } as never}
			onOk={handleSave}
			onCancel={onClose}
			destroyOnClose
			width={520}
		>
			<Space direction="vertical" size={12} style={{ display: "flex" }} data-testid="appointments-add-availability-modal">
				<Typography.Text>
					{member ? `${member.first_name} ${member.last_name}`.trim() : pickable ? "Choose a practitioner" : "This practitioner"} · {dateKey} · {dragged.start} – {dragged.end}
				</Typography.Text>

				{pickable && (
					<div>
						<Typography.Text type="secondary">Practitioner</Typography.Text>
						<Select
							value={practitionerId || undefined}
							onChange={setPractitionerId}
							disabled={!canManageSchedule}
							showSearch
							optionFilterProp="label"
							placeholder="Choose a practitioner"
							style={{ width: "100%" }}
							data-testid="appointments-add-availability-practitioner"
							options={practitionerOptions}
						/>
					</div>
				)}

				{loading && <Spin />}

				{!loading && loadFailed && (
					<Alert type="error" showIcon message="The existing schedule for this day could not be read" description="Try again — adding availability without it would overwrite whatever is already there." data-testid="appointments-add-availability-error" />
				)}

				{!loading && refused && existing && (
					<Alert
						type="warning"
						showIcon
						data-testid="appointments-add-availability-refused"
						message="This day already has a schedule override"
						description={
							<>
								<div>
									A <strong>{existing.override_type}</strong> override covers {String(existing.start_date).slice(0, 10)} to {String(existing.end_date).slice(0, 10)}
									{existing.reason ? ` — ${existing.reason}` : ""}.
								</div>
								<div style={{ marginTop: 6 }}>
									Adding availability here would rewrite that whole period, so it is left alone. Change it in the schedule editor instead
									{userId === practitionerId ? <> — <Link to="/profile">open your profile schedule</Link>.</> : " — the practitioner's Availability tab in Team management."}
								</div>
							</>
						}
					/>
				)}

				{!loading && !refused && !loadFailed && !!practitionerId && (
					<div>
						<Typography.Text type="secondary">Working hours for this day after the change</Typography.Text>
						<div style={{ marginTop: 6 }} data-testid="appointments-add-availability-preview">
							{merged.map((window) => (
								<Tag key={`${window.start}-${window.end}`}>{`${window.start} – ${window.end}`}</Tag>
							))}
						</div>
						{existing === null && <Typography.Text type="secondary" style={{ display: "block", marginTop: 8 }}>This creates a one-day schedule override; the rest of the week is unchanged.</Typography.Text>}
					</div>
				)}
			</Space>
		</Modal>
	);
};

export default AddAvailabilityModal;
